'use client';
import React from 'react';
import { SessionContextProvider, SessionContextProviderProps, } from '@supabase/auth-helpers-react';
import supabaseClient from '@/utils/supabase-browser';
import { SupabaseListener } from '@/components/SupabaseListener';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import ReactNoSSR from 'react-no-ssr';
import { User } from '@supabase/supabase-js'
// import { MaintenanceModeContextProvider } from '@/contexts/MaintenanceModeContext';
// import AdminBannerOverlay from '@/components/presentational/tailwind/AdminBannerOverlay';
import ModalContextProvider from "@/contexts/ModalContextProvider";
import ConfettiContextProvider from "@/contexts/ConfettiContextProvider";
import { DEMO_USER_EMAIL } from '@/constants';
import { usePathname } from 'next/navigation';

// Create a client
const queryClient = new QueryClient();

// User context
export const UserContext = React.createContext(null);

/**
 * This is a wrapper for the app that provides the supabase client, the router event wrapper
 * the react-query client, supabase listener, and the navigation progress bar.
 *
 * The listener is used to listen for changes to the user's session and update the UI accordingly.
 */
export default function AppProviders({
  // Layouts must accept a children prop.
  // This will be populated with nested layouts or pages
  children,
  initialSession,
  user,
  initialIsAppInMaintenanceMode,
}: {
  children: React.ReactNode;
  initialSession: Pick<SessionContextProviderProps, 'initialSession'>['initialSession'];
  user?: User;
  initialIsAppInMaintenanceMode?: boolean;
}) {
  // console.log('AppProviders');
  // console.log('initialSession', initialSession);
  const pathname = usePathname();
  const isDemoUser = initialSession && initialSession.user.email === DEMO_USER_EMAIL
  const ConditionalModalContextProvider = ({ children, isDemoUser }) => {
    // Don't render modals for demo user, as they won't be used
    if (isDemoUser || pathname === '/start')
      return children;
    return <ModalContextProvider>{children}</ModalContextProvider>;
  };

  return (
    <QueryClientProvider client={queryClient}>
      <SessionContextProvider supabaseClient={supabaseClient} initialSession={initialSession}>
        <SupabaseListener accessToken={initialSession?.access_token} />
        <UserContext.Provider value={user}>
          <ConditionalModalContextProvider isDemoUser={isDemoUser}>
            <ConfettiContextProvider>
              {children}
              <ReactNoSSR>
                <Toaster position='bottom-center'
                  toastOptions={{
                    className: '!rounded-lg text-sm font-medium gap-1 !px-3',
                    style: { boxShadow: '0px 0px 0px 1px rgba(47, 10, 154, 0.08), 0px 1px 2px 0px rgba(47, 10, 154, 0.10)' }
                  }}
                />
                {/* <AdminBannerOverlay /> */}
              </ReactNoSSR>
            </ConfettiContextProvider>
          </ConditionalModalContextProvider>
        </UserContext.Provider>
      </SessionContextProvider>
    </QueryClientProvider>
  );
}